import React, { useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { OPPORTUNITY_TYPES } from "@/lib/options";
import { Sparkles, ArrowLeft, Send } from "lucide-react";

export default function PostOpportunity() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", type: "styled_shoot",
    location_city: "", location_state: "", location_country: "",
    event_date: "", deadline: "", compensation: "", requirements: "", cover_image: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/opportunities", form);
      nav(`/dashboard/vendor/opportunity/${data.opp_id}`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to post.");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <Link to="/dashboard/vendor" className="inline-flex items-center gap-2 text-muted hover:text-ink mb-6 text-sm" data-testid="post-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back to dashboard
        </Link>
        <p className="qld-overline mb-3">new opportunity</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ink mb-3">Post the gig.</h1>
        <p className="text-muted mb-10">Lovers will see this on the opportunities board and can apply directly.</p>

        <form onSubmit={submit} className="space-y-5">
          <Field label="Title"><input required className="qld-input" placeholder="e.g. Spring styled shoot — Brooklyn rooftop"
            value={form.title} onChange={(e)=>set("title", e.target.value)} data-testid="post-title"/></Field>

          <Field label="Type">
            <select className="qld-input" value={form.type} onChange={(e)=>set("type", e.target.value)} data-testid="post-type">
              {OPPORTUNITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>

          <Field label="Description">
            <textarea required rows={6} className="qld-input" placeholder="Vision, vibe, what you're looking for in lovers, day-of expectations…"
              value={form.description} onChange={(e)=>set("description", e.target.value)} data-testid="post-description"/>
          </Field>

          <div className="grid md:grid-cols-3 gap-5">
            <Field label="City"><input className="qld-input" value={form.location_city} onChange={(e)=>set("location_city", e.target.value)} data-testid="post-city"/></Field>
            <Field label="State / Region"><input className="qld-input" value={form.location_state} onChange={(e)=>set("location_state", e.target.value)} data-testid="post-state"/></Field>
            <Field label="Country"><input className="qld-input" value={form.location_country} onChange={(e)=>set("location_country", e.target.value)} data-testid="post-country"/></Field>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Event date">
              <input type="date" className="qld-input" value={form.event_date} onChange={(e)=>set("event_date", e.target.value)} data-testid="post-date"/>
            </Field>
            <Field label="Application deadline">
              <input type="date" className="qld-input" value={form.deadline} onChange={(e)=>set("deadline", e.target.value)} data-testid="post-deadline"/>
            </Field>
          </div>

          <Field label="Compensation" hint="Paid, TFP (Time for prints/photos), travel covered, etc.">
            <input className="qld-input" placeholder="$200 + photos · or TFP · or travel covered"
              value={form.compensation} onChange={(e)=>set("compensation", e.target.value)} data-testid="post-comp"/>
          </Field>

          <Field label="Requirements (optional)">
            <textarea rows={3} className="qld-input" placeholder="Anything specific you're looking for?"
              value={form.requirements} onChange={(e)=>set("requirements", e.target.value)} data-testid="post-reqs"/>
          </Field>

          {err && <p className="text-destructive text-sm" data-testid="post-error">{err}</p>}

          <button disabled={busy} className="qld-btn-primary" data-testid="post-submit-btn">
            <Send className="w-4 h-4" strokeWidth={1.5}/> {busy ? "Posting…" : "Post opportunity"}
          </button>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink mb-1.5">{label}</span>
      {hint && <span className="block text-xs text-muted mb-2">{hint}</span>}
      {children}
    </label>
  );
}
